import React, { useContext, useRef, useState } from 'react';
import {
	View,
	Text,
	TouchableOpacity,
	Dimensions,
	FlatList,
	Platform,
} from 'react-native';

let Modal;
if (Platform.OS === 'web') {
	Modal = require('../dialog/Modal.web').default;
} else {
	Modal = require('react-native-modal').default;
}

import { ListContainer } from '../../containers';
import { TextInput } from '../input';
import { ListContext } from '../../../common/containers';
import PickerButton from '../picker/PickerButton/PickerButton';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useDidUpdateEffect } from '../../../common/utils/hooks';

const SearchDropdownSelectorItem = ({
	item,
	renderItem,
	disabledItemPredicate,
	onPress,
}) => {
	const _onPress = () => onPress(item);
	const styles = useThemedStyles(createStyles);

	// Clone the rendered item and apply text style
	const cloneWithTextStyle = (element) => {
		if (React.isValidElement(element) && element.type === Text) {
			// Avoid accessing element.ref which is deprecated in React 19
			const { style, ...restProps } = element.props;
			return React.cloneElement(element, {
				...restProps,
				style: [styles.itemText, style],
			});
		}
		return element;
	};

	const renderedItem = cloneWithTextStyle(renderItem(item));

	return disabledItemPredicate && disabledItemPredicate(item) ? (
		<View style={styles.item}>
			{renderedItem}
		</View>
	) : (
		<TouchableOpacity
			onPress={_onPress}
			style={styles.item}
		>
			{renderedItem}
		</TouchableOpacity>
	);
};

// Default trigger: a full-width field with the choice and a drop-down arrow.
const SelectorFieldButton = ({ label, onPress, disabled, style }) => (
	<PickerButton onPress={onPress} disabled={disabled} style={style} accessibilityRole="button">
		{label}
	</PickerButton>
);

const SearchDropdownSelector = ({
	initialValue,
	value,
	items,
	loading,
	renderLabel,
	renderItem,
	disabledItemPredicate,
	onChange,
	disabled,
	placeholder = 'Search',
	buttonStyle,
	customButton,
}) => {

	const { changeQueryFilterDebounced, triggerQuery } = useContext(ListContext) || {};
	const styles = useThemedStyles(createStyles);

	const [query, setQuery] = useState('');
	const [open, setOpen] = useState(false);
	const [internalValue, setInternalValue] = useState(initialValue || value);
	const pendingSelectionRef = useRef(null);

	useDidUpdateEffect(() => {
		setInternalValue(value);
	}, [value]);

	const _onChange = newValue => {
		setQuery(newValue);
		changeQueryFilterDebounced && changeQueryFilterDebounced(newValue);
	};

	const _openSearch = () => {
		if (triggerQuery) {
			triggerQuery();
		}
		setOpen(true);
	};

	const _closeSearch = () => {
		// The modal stays interactive during its exit animation, so cancel and
		// backdrop must close explicitly (a toggle could reopen it mid-dismissal)
		// and discard any selection still waiting for onModalHide.
		pendingSelectionRef.current = null;
		setOpen(false);
	};

	const _onPress = item => {
		if (Platform.OS === 'web') {
			setInternalValue(item);
			onChange(item);
			setOpen(false);
			return;
		}
		// Defer the selection until the picker's native modal has fully hidden
		// (onModalHide): consumers may close a parent dialog in onChange, and
		// dismissing two stacked native modals at once freezes iOS touch input.
		pendingSelectionRef.current = item;
		setOpen(false);
	};

	const _onModalHide = () => {
		const pendingSelection = pendingSelectionRef.current;
		if (pendingSelection) {
			pendingSelectionRef.current = null;
			setInternalValue(pendingSelection);
			onChange(pendingSelection);
		}
	};

	const _renderLabel = item => renderLabel(item);

	const _keyExtractor = item => item.id;

	const _renderItem = ({ item }) => {

		return (
			<SearchDropdownSelectorItem
				id={item.id}
				onPress={_onPress}
				item={item}
				renderItem={renderItem || _renderLabel}
				disabledItemPredicate={disabledItemPredicate}
			/>
		);
	};

	const { height } = Dimensions.get('window');

	const ButtonComponent = customButton || SelectorFieldButton;
	const trimmedQuery = query?.trim() || '';

	return (
		<>
			<ButtonComponent
				disabled={disabled}
				onPress={_openSearch}
				label={internalValue ? renderLabel(internalValue) : placeholder}
				style={buttonStyle}
			/>
			<Modal
				onBackdropPress={_closeSearch}
				onBackButtonPress={_closeSearch}
				onModalHide={_onModalHide}
				isVisible={open}
				animationIn="slideInDown"
				animationOut="slideOutUp"
				style={styles.modal}
			>
				<View style={styles.modalPanel}>
					<View style={styles.searchHeader}>
						<View style={styles.searchInputContainer}>
							<TextInput
								value={query}
								onChange={_onChange}
								placeholder="Search"
								style={styles.searchInput}
							/>
						</View>
						<TouchableOpacity
							style={styles.cancelButton}
							onPress={_closeSearch}
							accessibilityRole="button"
						>
							<Text style={styles.cancelText}>Cancel</Text>
						</TouchableOpacity>
					</View>
					<View
						keyboardShouldPersistTaps="handled"
						scrollIndicatorInsets={{ right: 1 }}
						style={[
							styles.listContainer,
							{
								maxHeight: height / 2,
							},
						]}
					>
						{
							loading &&
								<View style={styles.messageContainer}><Text style={styles.messageText}>Loading...</Text></View>
						}
						{
							!loading && items && items.length === 0 &&
								<View style={styles.messageContainer}>
									<Text style={styles.messageText}>
										{trimmedQuery ? 'No matches found' : 'No items available'}
									</Text>
								</View>
						}
						{
							!loading && items && items.length > 0 && (
								<FlatList
									keyboardShouldPersistTaps="handled"
									data={items}
									keyExtractor={_keyExtractor}
									renderItem={_renderItem}
								/>
							)
						}
					</View>
				</View>
			</Modal>
		</>
	);
};

const createStyles = ({ ui: { colors, radius, space, touch, type } }) => ({
	item: {
		minHeight: touch.min,
		justifyContent: 'center',
		paddingHorizontal: space.lg,
		paddingVertical: space.sm,
		borderBottomWidth: 1,
		borderBottomColor: colors.border,
	},
	itemText: {
		...type.rowText,
		color: colors.text,
	},
	modal: {
		margin: 0,
		justifyContent: 'center',
		paddingHorizontal: space.gutter,
		paddingTop: 30,
		paddingBottom: 20,
	},
	modalPanel: {
		backgroundColor: colors.surface,
		borderRadius: radius.hero,
		overflow: 'hidden',
		width: '100%',
		maxWidth: 650,
		alignSelf: 'center',
	},
	searchHeader: {
		flexDirection: 'row',
		paddingVertical: space.md,
		paddingLeft: space.lg,
		paddingRight: space.sm,
		alignItems: 'center',
		gap: space.xs,
	},
	searchInputContainer: {
		flex: 1,
	},
	searchInput: {
		marginBottom: 0,
	},
	cancelButton: {
		minHeight: touch.min,
		justifyContent: 'center',
		paddingHorizontal: space.sm,
	},
	cancelText: {
		...type.bodySmallStrong,
		color: colors.accent,
	},
	listContainer: {
		paddingBottom: 6,
	},
	messageContainer: {
		paddingHorizontal: space.lg,
		paddingVertical: space.md,
	},
	messageText: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
});

export default ListContainer(SearchDropdownSelector);
